<template>
  <section
    class="mfa-guide"
    aria-label="Authenticator setup guide"
  >
    <h5 class="mb-2">
      Follow along on your phone
    </h5>

    <p
      class="mfa-guide__note mb-3"
      :class="{ 'mfa-guide__note--warning': step === steps.length - 1 }"
      aria-label="Important setup information"
    >
      <font-awesome-icon
        :icon="step === steps.length - 1 ? ['fas', 'exclamation-triangle'] : ['fas', 'info-circle']"
        class="mfa-guide__note-icon"
      />
      <span>{{ steps[step].note }}</span>
    </p>

    <svg
      :key="step"
      viewBox="0 0 400 260"
      class="mfa-guide__scene"
      aria-hidden="true"
      focusable="false"
    >
      <ellipse
        cx="200"
        cy="243"
        rx="146"
        ry="10"
        class="mfa-guide__wash"
      />
      <rect
        x="45"
        y="13"
        width="170"
        height="229"
        rx="24"
        class="mfa-guide__phone"
      />
      <rect
        x="56"
        y="25"
        width="148"
        height="204"
        rx="16"
        class="mfa-guide__paper"
      />
      <path
        d="M110 33h40"
        class="mfa-guide__outline"
      />
      <text
        x="69"
        y="59"
        class="mfa-guide__small"
      >Authenticator</text>

      <g v-if="step === 0">
        <g class="mfa-guide__menu">
          <rect
            x="66"
            y="84"
            width="128"
            height="76"
            rx="10"
            class="mfa-guide__menu-panel"
          />
          <path
            d="M67 121h126"
            class="mfa-guide__divider"
          />
          <path
            d="M75 102h10m-5-5v10"
            class="mfa-guide__accent-line"
          />
          <text
            x="92"
            y="107"
            class="mfa-guide__small"
          >Add a code</text>
          <path
            d="M76 134h4m-4 0v4m9-4h-3m3 0v4m-9 6h4m-4 0v-3m9 3h-3m3 0v-3"
            class="mfa-guide__accent-line"
          />
          <text
            x="92"
            y="144"
            class="mfa-guide__small mfa-guide__bold"
          >Scan a QR code</text>
        </g>
        <circle
          cx="172"
          cy="198"
          r="19"
          class="mfa-guide__accent"
        />
        <path
          d="M163 198h18m-9-9v18"
          class="mfa-guide__white-line"
        />
        <text
          x="311"
          y="48"
          text-anchor="middle"
          class="mfa-guide__small mfa-guide__bold"
        >Your QR</text>
        <text
          x="311"
          y="64"
          text-anchor="middle"
          class="mfa-guide__small"
        >on this page</text>
        <g class="mfa-guide__page-preview">
          <rect
            x="233"
            y="76"
            width="157"
            height="142"
            rx="8"
            class="mfa-guide__paper mfa-guide__outline"
          />
          <text
            x="241"
            y="96"
            class="mfa-guide__small mfa-guide__bold mfa-guide__brand"
          >RingbaX</text>
          <path
            d="M234 108h155"
            class="mfa-guide__divider"
          />
          <rect
            x="240"
            y="115"
            width="30"
            height="96"
            rx="3"
            class="mfa-guide__wash"
          />
          <path
            d="M246 125h18m-18 13h14m-14 13h18m-18 13h14m-14 13h18"
            class="mfa-guide__outline"
          />
          <text
            x="329"
            y="127"
            text-anchor="middle"
            class="mfa-guide__small"
          >MFA setup</text>
          <g class="mfa-guide__qr">
            <path
              d="M304 137h18v18h-18zM336 137h18v18h-18zM304 169h18v18h-18z"
              class="mfa-guide__outline"
            />
            <path
              d="M309 142h8v8h-8zM341 142h8v8h-8zM309 174h8v8h-8zM327 137h4v13h-4zM304 160h10v4h-10zM319 159h12v9h-5v-4h-7zM336 160h8v7h10v5h-15v-7h-3zM327 174h6v13h-6zM339 177h6v5h9v5h-15zM349 159h5v5h-5z"
              class="mfa-guide__ink"
            />
          </g>
          <path
            d="M300 198h58"
            class="mfa-guide__divider"
          />
        </g>
        <path
          d="M299 134h60"
          class="mfa-guide__scan mfa-guide__accent-line"
        />
        <path
          d="M-6 28V6a6 6 0 0 1 12 0v17q4-5 9 0 6-3 10 4 10-1 10 9v10q0 10-8 16H5l-16-22q-4-7 2-9z"
          class="mfa-guide__hand mfa-guide__tap"
        />
      </g>

      <g v-else>
        <g
          v-if="step === 1"
          class="mfa-guide__old-entry"
        >
          <rect
            x="66"
            y="78"
            width="128"
            height="39"
            rx="8"
            class="mfa-guide__wash"
          />
          <text
            x="78"
            y="102"
            class="mfa-guide__small"
          >Old RingbaX entry</text>
        </g>
        <g class="mfa-guide__entry">
          <rect
            x="65"
            y="131"
            width="130"
            height="76"
            rx="10"
            class="mfa-guide__new-entry"
          />
          <text
            x="77"
            y="154"
            class="mfa-guide__small mfa-guide__bold"
          >RingbaX</text>
          <text
            x="77"
            y="172"
            class="mfa-guide__small"
          >Your email</text>
          <text
            x="77"
            y="196"
            class="mfa-guide__code"
          >123 456</text>
        </g>
        <g v-if="step === 1">
          <g class="mfa-guide__delete">
            <rect
              x="239"
              y="78"
              width="147"
              height="39"
              rx="8"
              class="mfa-guide__menu-panel"
            />
            <path
              d="M251 91h12m-9-3h6m-8 4 1 14h8l1-14m-7 3v8m3-8v8"
              class="mfa-guide__danger-line"
            />
            <text
              x="272"
              y="102"
              class="mfa-guide__small"
            >Delete old entry</text>
          </g>
          <path
            d="M286 170h-64m0 0 12-10m-12 10 12 10"
            class="mfa-guide__accent-line"
          />
          <text
            x="249"
            y="145"
            class="mfa-guide__small mfa-guide__bold"
          >Just added</text>
          <text
            x="249"
            y="165"
            class="mfa-guide__small"
          >Use this entry</text>
          <path
            d="M-6 28V6a6 6 0 0 1 12 0v17q4-5 9 0 6-3 10 4 10-1 10 9v10q0 10-8 16H5l-16-22q-4-7 2-9z"
            class="mfa-guide__hand mfa-guide__remove-tap"
          />
        </g>
        <g v-else>
          <rect
            x="239"
            y="48"
            width="147"
            height="162"
            rx="12"
            class="mfa-guide__paper mfa-guide__outline"
          />
          <text
            x="312"
            y="81"
            text-anchor="middle"
            class="mfa-guide__small mfa-guide__bold"
          >MFA setup</text>
          <g class="mfa-guide__success">
            <rect
              x="246"
              y="57"
              width="133"
              height="43"
              rx="6"
              class="mfa-guide__success-panel"
            />
            <path
              d="M254 77l5 5 9-12"
              class="mfa-guide__success-line"
            />
            <text
              x="276"
              y="74"
              class="mfa-guide__small mfa-guide__bold"
            >Success</text>
            <text
              x="276"
              y="91"
              class="mfa-guide__small"
            >Setup complete</text>
          </g>
          <text
            x="251"
            y="117"
            class="mfa-guide__small"
          >On this page</text>
          <rect
            x="251"
            y="128"
            width="121"
            height="31"
            rx="5"
            class="mfa-guide__wash"
          />
          <text
            v-for="(digit, index) in '123456'"
            :key="index"
            :x="260 + index * 17"
            y="151"
            class="mfa-guide__digit mfa-guide__code"
            :style="{ animationDelay: `${index * 0.35}s` }"
          >{{ digit }}</text>
          <rect
            x="251"
            y="170"
            width="121"
            height="27"
            rx="6"
            class="mfa-guide__accent"
          />
          <text
            x="312"
            y="188"
            text-anchor="middle"
            class="mfa-guide__button-text"
          >Verify Code</text>
          <path
            d="M-6 28V6a6 6 0 0 1 12 0v17q4-5 9 0 6-3 10 4 10-1 10 9v10q0 10-8 16H5l-16-22q-4-7 2-9z"
            class="mfa-guide__hand mfa-guide__verify-tap"
          />
        </g>
      </g>
      <circle
        cx="85"
        cy="218"
        r="2"
        class="mfa-guide__ink"
      />
      <circle
        cx="99"
        cy="218"
        r="2"
        class="mfa-guide__ink"
      />
      <path
        d="M88 222q4 4 8 0"
        class="mfa-guide__outline"
      />
    </svg>

    <div
      aria-live="polite"
      aria-atomic="true"
      class="mfa-guide__instruction mt-3"
    >
      <div class="mfa-guide__eyebrow">
        Step {{ step + 1 }} of {{ steps.length }}
      </div>
      <h6>{{ steps[step].title }}</h6>
      <ul class="mfa-guide__list mb-0">
        <li
          v-for="item in steps[step].items"
          :key="item"
        >
          {{ item }}
        </li>
      </ul>
    </div>
    <div class="mfa-guide__controls mt-3">
      <button
        v-if="step < steps.length - 1"
        type="button"
        class="custom-button--flat mfa-guide__next-button"
        @click="step++"
      >
        Next step
      </button>
      <button
        v-else
        type="button"
        class="custom-button--flat mfa-guide__next-button"
        @click="step = 0"
      >
        Watch again
      </button>
    </div>
    <details class="mfa-guide__faq mt-3">
      <summary>Having trouble?</summary>
      <div class="mfa-guide__faq-body">
        <details>
          <summary>The code is invalid</summary>
          <p>Use the newest RingbaX entry for your email. Enter exactly 6 digits with no spaces.</p>
        </details>

        <details>
          <summary>I scanned the QR code but never saw success</summary>
          <p>Scanning only adds the entry to your phone. Enter its current code in the setup form and click Verify Code.</p>
        </details>

        <details>
          <summary>My session expired</summary>
          <p>Sign in again, reopen MFA setup, and scan the current QR code.</p>
        </details>
      </div>
    </details>
  </section>
</template>

<script>
export default {
  name: 'MFASetupGuide',
  data() {
    return {
      step: 0,
      steps: [
        {
          title: 'Scan this page’s QR code',
          note: 'Scan the QR code shown on this page. Do not reuse an old RingbaX entry.',
          items: ['Open your authenticator app.', 'Tap +, then Scan a QR code.', 'Scan the QR code in the setup form.']
        },
        {
          title: 'Use the newest RingbaX entry',
          note: 'Keep the newest RingbaX entry for your email and remove older entries for this account.',
          items: ['Keep the RingbaX entry for your email.', 'Delete older RingbaX entries for this account.']
        },
        {
          title: 'Enter the code and finish setup',
          note: 'MFA setup is not finished until this page confirms the code was accepted.',
          items: ['Enter the current 6-digit code with no spaces.', 'Click Verify Code in the setup form.', 'Wait for the success message before leaving this page.']
        }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
.mfa-guide {
  position: relative;
  padding: 1.25rem;
  background: rgba($white, .08);
  border: 1px solid var(--component-card__header-border-color);
  min-width: 0;

  &__controls { display: flex; justify-content: flex-end; }
  &__next-button {
    min-height: 44px;
    padding-right: 24px;
    padding-left: 24px;
    background: $success;
    border-color: $success;
    border-radius: 2px;
    color: $white;
  }
  &__next-button:hover, &__next-button:active {
    background: darken($success, 8%);
    border-color: darken($success, 8%);
    color: $white;
  }
  &__next-button:focus-visible { outline: 3px solid currentColor; outline-offset: 3px; }
  &__scene { display: block; width: 100%; max-width: 400px; height: auto; margin: 1rem auto 0; background: $white; }
  &__phone, &__ink { fill: $dark; }
  &__wash { fill: $light-bg-dark; }
  &__accent { fill: $primary; }
  &__outline { fill: none; stroke: $dark; stroke-width: 2; stroke-linecap: round; }
  &__paper { fill: $white; }
  &__hand { fill: $warning; stroke: $dark; stroke-width: 2; stroke-linejoin: round; }
  &__accent-line { stroke: $primary; stroke-width: 3; fill: none; stroke-linecap: round; }
  &__white-line { stroke: $white; stroke-width: 3; }
  &__small { font-size: 12px; fill: $dark; }
  &__bold { font-weight: 700; }
  &__code { font-size: 24px; fill: $primary; font-weight: 700; }
  &__button-text { font-size: 12px; fill: $white; }
  &__new-entry { fill: $white; stroke: $primary; stroke-width: 3; }
  &__menu-panel { fill: $white; stroke: $dark; stroke-width: 1; filter: drop-shadow(0 3px 3px rgba($dark, .15)); }
  &__divider { stroke: $light-bg-dark; stroke-width: 2; }
  &__danger-line { fill: none; stroke: $danger; stroke-width: 2; stroke-linecap: round; }
  &__success-panel { fill: mix($success, $white, 12%); stroke: $success; }
  &__success-line { fill: none; stroke: $dark; stroke-width: 3; stroke-linecap: round; stroke-linejoin: round; }
  &__note {
    display: flex;
    align-items: flex-start;
    gap: .5rem;
    padding: .5rem .75rem;
    background: rgba($white, .06);
    color: inherit;
    font-size: .875rem;
  }
  &__note--warning {
    background: rgba($warning, .14);
    font-weight: 600;
  }
  &__note-icon {
    margin-top: .15rem;
    color: $success;
    flex: 0 0 auto;
  }
  &__note--warning &__note-icon { color: $warning; }
  &__instruction { min-height: 7rem; }
  &__eyebrow { margin-bottom: .35rem; font-size: .75rem; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; }
  &__list { padding-left: 1.25rem; }
  &__list li + li { margin-top: .35rem; }
  &__faq { border-top: 1px solid var(--component-card__header-border-color); }
  &__faq summary { padding: .75rem 0; min-height: 44px; cursor: pointer; font-weight: 600; }
  &__faq summary:focus-visible { outline: 3px solid currentColor; outline-offset: 3px; }
  &__faq-body details { border-top: 1px solid var(--component-card__header-border-color); }
  &__faq-body summary { font-weight: 400; }
  &__faq-body p { margin-bottom: .75rem; }
  &__menu { animation: mfa-menu 8s infinite; }
  &__tap { animation: mfa-tap 8s infinite; }
  &__scan { animation: mfa-scan 8s infinite; }
  &__old-entry { animation: mfa-remove 8s infinite; }
  &__delete { animation: mfa-delete 8s infinite; }
  &__remove-tap { animation: mfa-remove-tap 8s infinite; }
  &__entry { animation: mfa-highlight 8s infinite; transform-origin: 130px 170px; }
  &__digit { animation: mfa-type 8s step-end infinite backwards; }
  &__verify-tap { animation: mfa-verify-tap 8s infinite; }
  &__success { animation: mfa-success 8s step-end infinite; }
}
@keyframes mfa-menu { 0%, 24%, 95%, 100% { opacity: 0; } 28%, 90% { opacity: 1; } }
@keyframes mfa-tap {
  0%, 8% { opacity: 0; transform: translate(182px, 218px); }
  16%, 24% { opacity: 1; transform: translate(172px, 198px); }
  32% { opacity: 1; transform: translate(182px, 210px); }
  44%, 52% { opacity: 1; transform: translate(145px, 140px); }
  62%, 100% { opacity: 0; transform: translate(155px, 156px); }
}
@keyframes mfa-scan {
  0%, 56%, 94%, 100% { opacity: 0; transform: translateY(0); }
  62% { opacity: 1; transform: translateY(0); }
  88% { opacity: 1; transform: translateY(56px); }
}
@keyframes mfa-remove {
  0%, 53%, 100% { opacity: 1; transform: translateX(0); }
  63%, 94% { opacity: 0; transform: translateX(-20px); }
}
@keyframes mfa-delete { 0%, 24%, 64%, 100% { opacity: 0; } 30%, 54% { opacity: 1; } }
@keyframes mfa-remove-tap {
  0%, 6% { opacity: 0; transform: translate(145px, 117px); }
  14%, 26% { opacity: 1; transform: translate(130px, 99px); }
  42%, 52% { opacity: 1; transform: translate(310px, 99px); }
  62%, 100% { opacity: 0; transform: translate(320px, 119px); }
}
@keyframes mfa-highlight { 65%, 85% { transform: scale(1.03); } }
@keyframes mfa-type { 0%, 85%, 100% { opacity: 0; } 20% { opacity: 1; } }
@keyframes mfa-verify-tap {
  0%, 43% { opacity: 0; transform: translate(331px, 203px); }
  53%, 59% { opacity: 1; transform: translate(312px, 182px); }
  64% { opacity: 1; transform: translate(312px, 186px); }
  70%, 100% { opacity: 0; transform: translate(331px, 203px); }
}
@keyframes mfa-success { 0%, 96%, 100% { opacity: 0; } 70% { opacity: 1; } }
@media (prefers-reduced-motion: reduce) {
  .mfa-guide * { animation: none !important; }
  .mfa-guide__hand, .mfa-guide__scan { display: none; }
}
</style>
